import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CapsuleButton } from "../../Common/_components";
import { SegmentedControl } from "../../Common/_components/SegmentedControl";
import { Selector } from "../../Common/_components/Selector";
import { Icons } from "../../Common/_assets/icons/Icons";
import { CustomModal } from "../../Everou/_components/CustomModal";
import { Selectors } from "../../Everou/_reducers/app.reducer";
import { OrganizationsActions } from "../../Organizations/_actions/OrganizationsActions";
import {
    ORGANIZATION_MEMBER_ROLES,
    OrganizationDetail,
    OrganizationMemberRole,
} from "../../Organizations/_models/Organization";
import { useOrganizationPermissions } from "../_helpers/organizationPermissions";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function PMSOrganizationMembers() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const selectedUid: string | null = useSelector(
        Selectors.getOrganizationSelectedUid,
    );
    const detail: OrganizationDetail | null = useSelector(
        Selectors.getOrganizationSelectedDetail,
    );
    // Adding and removing members is owner-only on the backend (error 780).
    const { canManage } = useOrganizationPermissions();

    const [isAddOpen, setIsAddOpen] = useState(false);
    const [email, setEmail] = useState("");
    // Admin by default: adding a member always made an admin before roles.
    const [role, setRole] = useState<OrganizationMemberRole>("admin");
    // Role picked per member while the change is saving: it grants or cancels
    // an invitation in every location and can take seconds.
    const [pendingRoles, setPendingRoles] = useState<
        Record<string, OrganizationMemberRole>
    >({});
    // A role change applies to every location and restores access cancelled
    // by hand, so it waits for the owner to confirm it.
    const [roleChange, setRoleChange] = useState<{
        userUid: string;
        role: OrganizationMemberRole;
    } | null>(null);
    const [roleFilter, setRoleFilter] = useState<
        OrganizationMemberRole | "all"
    >("all");

    useEffect(() => {
        if (isAddOpen) {
            setEmail("");
            setRole("admin");
        }
    }, [isAddOpen]);

    function handleAdd() {
        // Defence in depth, like handleSaveSettings: today the modal only
        // opens from the gated button, but a second caller would otherwise
        // reopen the hole.
        if (!selectedUid || !canManage) return;
        const trimmed = email.trim();
        if (!EMAIL_REGEX.test(trimmed)) return;
        dispatch(
            OrganizationsActions.addUserToOrganization(
                selectedUid,
                trimmed,
                role,
            ),
        );
        setIsAddOpen(false);
    }

    async function handleChangeRole(
        userUid: string,
        newRole: OrganizationMemberRole,
    ) {
        // Owner-only on the backend (error 780), like adding and removing.
        if (!selectedUid || !canManage) return;
        setPendingRoles((pending) => ({ ...pending, [userUid]: newRole }));
        try {
            await dispatch(
                OrganizationsActions.changeMemberRole(
                    selectedUid,
                    userUid,
                    newRole,
                ),
            );
        } finally {
            setPendingRoles((pending) => {
                const rest = { ...pending };
                delete rest[userUid];
                return rest;
            });
        }
    }

    function handleConfirmRoleChange() {
        if (!roleChange) return;
        setRoleChange(null);
        handleChangeRole(roleChange.userUid, roleChange.role);
    }

    function roleOptions() {
        return ORGANIZATION_MEMBER_ROLES.map((value) => ({
            value,
            label: t(`mod_organizations_role_${value}`),
        }));
    }

    function handleRemove(userUid: string) {
        if (!selectedUid) return;
        dispatch(
            OrganizationsActions.removeUserFromOrganization(
                selectedUid,
                userUid,
            ),
        );
    }

    const allMembers = detail?.users ?? [];
    const members =
        roleFilter === "all"
            ? allMembers
            : allMembers.filter((user) => user.role === roleFilter);
    const ownerUid = detail?.ownerUid ?? null;
    const canAddMembers = detail?.canAddMembers ?? true;
    const isEmailValid = EMAIL_REGEX.test(email.trim());

    return (
        <div className="d-flex flex-column">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
                <div
                    data-testid="organization-members-role-filter"
                    aria-label={t("mod_organizations_role")}
                    className="mr-2 mb-1"
                >
                    <SegmentedControl
                        defaultSelected={{ value: roleFilter }}
                        segments={[
                            {
                                value: "all",
                                label: t("mod_organizations_role_filter_all"),
                            },
                            ...roleOptions(),
                        ]}
                        onSelect={setRoleFilter}
                    />
                </div>
                <CapsuleButton
                    testId={"organization-members-add"}
                    text={t("mod_organizations_add_member")}
                    onClick={() => setIsAddOpen(true)}
                    isEnabled={canAddMembers && canManage}
                    tooltip={addMemberTooltip()}
                    boostedToolTip={!!addMemberTooltip()}
                />
            </div>

            {members.length === 0 ? (
                <div className="text-center text-muted py-3">
                    {allMembers.length === 0
                        ? t("mod_organizations_no_members")
                        : t("mod_organizations_no_members_with_role")}
                </div>
            ) : (
                members.map((user) => (
                    <div
                        key={user.uid}
                        className="
                            d-flex
                            align-items-center
                            justify-content-between
                            border
                            rounded
                            p-2
                            mb-2
                            bg-white
                        "
                    >
                        {/* An email has no spaces: without these it pushes
                            the role selector out of the row on a phone. */}
                        <div
                            className="d-flex flex-column text-break mr-2"
                            style={{ minWidth: 0 }}
                        >
                            <div className="font-weight-bold">
                                {user.name || user.email}
                                {user.uid === ownerUid && (
                                    <span className="badge badge-pill badge-secondary ml-2">
                                        {t("mod_organizations_owner_badge")}
                                    </span>
                                )}
                            </div>
                            {user.name && (
                                <div className="text-muted small">
                                    {user.email}
                                </div>
                            )}
                        </div>
                        <div className="d-flex align-items-center">
                            {user.uid !== ownerUid && (
                                <div
                                    data-testid={`organization-members-role-${user.uid}`}
                                    className="mr-2"
                                    style={{ width: "10rem" }}
                                    title={
                                        canManage
                                            ? t(
                                                  `mod_organizations_role_${user.role}_description`,
                                              )
                                            : t(
                                                  "error_780_organization_requires_owner",
                                              )
                                    }
                                >
                                    <Selector
                                        aria-label={`${t(
                                            "mod_organizations_role",
                                        )}: ${user.email}`}
                                        isSearchable={false}
                                        options={roleOptions()}
                                        value={roleOptions().find(
                                            (option) =>
                                                option.value ===
                                                (pendingRoles[user.uid] ??
                                                    user.role),
                                        )}
                                        isDisabled={
                                            !canManage ||
                                            !!pendingRoles[user.uid]
                                        }
                                        onChange={(option: any) =>
                                            option.value !== user.role &&
                                            setRoleChange({
                                                userUid: user.uid,
                                                role: option.value,
                                            })
                                        }
                                    />
                                </div>
                            )}
                            <CapsuleButton
                                testId={`organization-members-remove-${user.uid}`}
                                icon={Icons.trash}
                                style={"DANGER"}
                                size={"SMALL"}
                                // Without it the row squashes the circle into
                                // an oval next to the role selector.
                                classNameExtra="flex-shrink-0"
                                tooltip={
                                    canManage
                                        ? t("mod_organizations_remove_member")
                                        : t(
                                              "error_780_organization_requires_owner",
                                          )
                                }
                                boostedToolTip={!canManage}
                                isEnabled={canManage}
                                onClick={() => handleRemove(user.uid)}
                                isVisible={user.uid !== ownerUid}
                            />
                        </div>
                    </div>
                ))
            )}

            <CustomModal
                show={!!roleChange}
                onHide={() => setRoleChange(null)}
                title={t("mod_organizations_role")}
                content={
                    roleChange && (
                        <div className="d-flex flex-column">
                            <div className="font-weight-bold">
                                {t(`mod_organizations_role_${roleChange.role}`)}
                            </div>
                            <div className="text-muted small mb-3">
                                {t(
                                    `mod_organizations_role_${roleChange.role}_description`,
                                )}
                            </div>
                            <div>
                                {t("mod_organizations_role_change_warning")}
                            </div>
                        </div>
                    )
                }
                footer={
                    <div className="d-flex">
                        <CapsuleButton
                            testId={"organization-members-role-cancel"}
                            text={t("global_cancel")}
                            style={"INFO"}
                            onClick={() => setRoleChange(null)}
                        />
                        <div className="ml-2" />
                        <CapsuleButton
                            testId={"organization-members-role-confirm"}
                            text={t("global_accept")}
                            onClick={handleConfirmRoleChange}
                        />
                    </div>
                }
            />

            <CustomModal
                show={isAddOpen}
                onHide={() => setIsAddOpen(false)}
                title={t("mod_organizations_add_member")}
                content={
                    <div className="d-flex flex-column">
                        <label className="font-weight-bold text-uppercase">
                            {t("mod_organizations_member_email")}
                        </label>
                        <input
                            data-testid="organization-members-add-email"
                            type="email"
                            className="form-control"
                            value={email}
                            autoFocus
                            placeholder={t(
                                "mod_organizations_member_email_placeholder",
                            )}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleAdd();
                            }}
                        />
                        <label className="font-weight-bold text-uppercase mt-3">
                            {t("mod_organizations_role")}
                        </label>
                        <div data-testid="organization-members-add-role">
                            <SegmentedControl
                                defaultSelected={{ value: role }}
                                segments={roleOptions()}
                                onSelect={setRole}
                                equalSegments
                                fullWidth
                            />
                        </div>
                        <div className="text-muted small mt-1">
                            {t(`mod_organizations_role_${role}_description`)}
                        </div>
                    </div>
                }
                footer={
                    <div className="d-flex">
                        <CapsuleButton
                            text={t("global_cancel")}
                            style={"INFO"}
                            onClick={() => setIsAddOpen(false)}
                        />
                        <div className="ml-2" />
                        <CapsuleButton
                            testId={"organization-members-add-submit"}
                            text={t("mod_organizations_add_member")}
                            onClick={handleAdd}
                            isEnabled={isEmailValid}
                        />
                    </div>
                }
            />
        </div>
    );

    // The Premium/location gate and the owner gate can both be closed; the
    // ownership one is the blocking condition the member can do nothing
    // about, so it wins the explanation.
    function addMemberTooltip(): string | undefined {
        if (!canManage) return t("error_780_organization_requires_owner");
        if (!canAddMembers)
            return t("mod_organizations_add_member_requires_location");
        return undefined;
    }
}
